<template>
  <form @submit="handleSubmit">
    <detail-data
      @change="handleChangeDetailData"
      :errors="employeeDataErrors"
      :value="employeeData"
      :isUnsavedChanges="changedFields.detailDataChanged"
      :title="!employee ? 'Create Employee Profile' : 'Employee Profile'"
    />
    <br />
    <roles-and-permission
      @change="handleChangePermission"
      :permissionValue="permissionData"
      :isUnsavedChanges="changedFields.permissionChanged"
    />
    <br />
    <div class="d-flex justify-content-end" style="gap: 10px">
      <custom-button
        @click="handleDiscard"
        :type="'button'"
        color="ghost"
        variant="outlined"
      >
        Discard All Changes
      </custom-button>
      <custom-button
        type="submit"
        buttonClass="d-flex align-items-center"
        :buttonStyle="{ gap: '8px' }"
        :disabled="
          !changedFields.detailDataChanged && !changedFields.permissionChanged
        "
      >
        <img src="~@/assets/icon/save-white.svg" alt="" />
        <p class="m-0">Save All Changes</p>
      </custom-button>
    </div>
  </form>
</template>

<script>
import DetailData from "./DetailData.vue";
import RolesAndPermission from "./RolesAndPermissions";
import { CustomButton } from "../../Button";
import {
  validate,
  email,
  minLength,
  required,
  phone,
} from "../../../utils/objectValidation";
import { alert } from "../../../utils";

export default {
  name: "Form",
  props: {
    employee: {
      type: Object,
      default: () => ({
        fullName: "",
        email: "",
        phone: "",
        birthDate: "",
        type: "",
        joinDate: "",
        division: "",
        workLocation: [],
        permissions: {
          internal: [],
          mobile: [],
          desktop: [],
        },
      }),
      required: false,
    },
  },
  components: {
    DetailData,
    RolesAndPermission,
    CustomButton,
  },
  data() {
    return {
      employeeData: {
        fullName: "",
        email: "",
        phone: "",
        birthDate: "",
        type: "",
        joinDate: "",
        division: "",
        workLocation: [],
      },
      employeeDataErrors: {
        fullName: null,
        email: null,
        phone: null,
        birthDate: null,
        type: null,
        joinDate: null,
        division: null,
        workLocation: null,
      },
      dataValidation: {
        fullName: [required()],
        email: [required(), email()],
        phone: [required(), phone()],
        birthDate: [required()],
        type: [required()],
        joinDate: [required()],
        division: [required()],
        workLocation: [required(), minLength(1)],
      },
      permissionData: {
        internal: [],
        mobile: [],
        desktop: [],
      },
    };
  },
  computed: {
    changedFields() {
      if (!this.employee)
        return { detailDataChanged: false, permissionChanged: false };
      let detailDataChanged = false;
      let permissionChanged = false;
      const { permissions, ...detailData } = this.employee;

      // Check if detail data has changed
      const detailDataKeys = Object.keys(this.employeeData);
      for (let i = 0; i < detailDataKeys.length; i++) {
        if (detailData[detailDataKeys[i]] instanceof Array) {
          const prevArray = detailData[detailDataKeys[i]].map(el => el.value).sort().join(",");
          const newArray = this.employeeData[detailDataKeys[i]].map(el => el.value)
            .sort()
            .join(",");
          if (prevArray !== newArray) {
            detailDataChanged = true;
            break;
          }
        } else if (
          detailData[detailDataKeys[i]] !== this.employeeData[detailDataKeys[i]]
        ) {
          detailDataChanged = true;
          break;
        }
      }

      // Check if permission has changed
      const permissionKeys = Object.keys(this.permissionData);
      for (let i = 0; i < permissionKeys.length; i++) {
        const parsedCurrentPermissions = permissions[permissionKeys[i]]
          .sort()
          .join(",");
        const parsedNewPermissions = this.permissionData[permissionKeys[i]]
          .sort()
          .join(",");
        if (parsedCurrentPermissions !== parsedNewPermissions) {
          permissionChanged = true;
          break;
        }
      }
      return {
        detailDataChanged,
        permissionChanged,
      };
    },
  },
  methods: {
    handleDiscard() {
      alert({ confirmButtonColor: "orange" })
        .fire({
          title: "Discard Changes",
          text: "Are you really okay with not saving all of these changes?",
          icon: "warning",
          showCancelButton: true,
          confirmButtonText: "Discard",
          cancelButtonText: "Cancel",
        })
        .then((result) => {
          if (result.isConfirmed) {
            this.$router.push({
              path: "/employee/search",
              query: {
                employeeName: "",
                employeeEmail: "",
                userType: "",
                division: "",
                page: 1,
              },
            });
          }
        });
    },
    async handleSubmit(event) {
      event.preventDefault();
      const { isValid, errors } = validate(
        this.employeeData,
        this.dataValidation
      );
      if (!isValid) {
        this.employeeDataErrors = errors;
        alert({ confirmButtonColor: "red" }).fire({
          title: "Error",
          text: "Please fill in all the required fields",
          icon: "error",
          confirmButtonText: "Okay",
        });
        return;
      }
      const parseWorkLocation = this.employeeData.workLocation.map(
        (location) => location.value
      );
      const sentData = {
        ...this.employeeData,
        region: parseWorkLocation,
        userType: this.employeeData.type,
        phoneNumber: "62" + this.employeeData.phone,
        selectRoles: {
          mobileAccess: this.permissionData.mobile,
          desktopAccess: this.permissionData.desktop,
          internalAccess: this.permissionData.internal,
        },
      };
      delete sentData.phone;
      delete sentData.type;
      this.$emit("submit", sentData);
    },
    handleChangeDetailData(values) {
      this.employeeData = values;
    },
    handleChangePermission(values, type) {
      this.permissionData[type] = values;
    },
  },
  watch: {
    employee(val) {
      if (val) {
        this.employeeData = {
          fullName: val.fullName,
          email: val.email,
          phone: val.phone,
          birthDate: val.birthDate,
          type: val.type,
          joinDate: val.joinDate,
          division: val.division,
          workLocation: val.workLocation,
        };
        this.permissionData = {
          mobile: [...val.permissions.mobile],
          desktop: [...val.permissions.desktop],
          internal: [...val.permissions.internal],
        };
      }
    },
  },
};
</script>


// WEBPACK FOOTER //
// src/components/Employee/Components/Form.vue